import { currentQueuedModalAtom } from '@/atoms/modalQueueAtoms';
import {
  hasImportedSharedTemplateAtom,
  pendingSharedTemplateAtom,
} from '@/atoms/sharedTemplateAtoms';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useImportSharedTemplate } from '@heidi/templates';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Component that monitors for pending shared template imports.
 * Triggers the import only after:
 * 1. User is authenticated
 * 2. All queued modals have been dismissed
 * 3. There's pending template data in sessionStorage
 * 4. User came from the shared template flow (from=shared-template param)
 */
export function PendingSharedTemplateImporter() {
  const router = useRouter();
  const onNavigate = useCallback(
    (href: string) => router.push(href).then(() => undefined),
    [router]
  );

  const { data: user } = useQueryUser();
  const { isAuthenticated: isTokenAuthenticated } = useIsAuthenticated();
  const currentModal = useAtomValue(currentQueuedModalAtom);
  const [pendingTemplate, setPendingTemplate] = useAtom(pendingSharedTemplateAtom);
  const setHasImportedSharedTemplate = useSetAtom(hasImportedSharedTemplateAtom);

  const hasTriggeredImport = useRef(false);

  const handleImportSuccess = useCallback(() => {
    void setPendingTemplate(null);
    void setHasImportedSharedTemplate(true);
  }, [setPendingTemplate, setHasImportedSharedTemplate]);

  const handleImportError = useCallback(() => {
    void setPendingTemplate(null);
  }, [setPendingTemplate]);

  const { importTemplate } = useImportSharedTemplate(pendingTemplate, {
    onNavigate,
    onSuccess: handleImportSuccess,
    onError: handleImportError,
  });

  const isAuthenticated = isTokenAuthenticated && !!user;
  const noModalsShowing = currentModal === null;
  const isFromSharedTemplate = router.query.from === 'shared-template';

  useEffect(() => {
    if (
      !isAuthenticated ||
      !noModalsShowing ||
      !isFromSharedTemplate ||
      !pendingTemplate ||
      hasTriggeredImport.current
    ) {
      return;
    }

    hasTriggeredImport.current = true;
    void importTemplate();
  }, [
    isAuthenticated,
    noModalsShowing,
    isFromSharedTemplate,
    pendingTemplate,
    importTemplate,
  ]);

  return null;
}
